import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/FormField';
import { useToast } from '../../../components/ui/Toast';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { useLeads, useStartLeadProgress, useQualifyLead, useDisqualifyLead, useDeleteLead } from '../hooks';
import { EditLeadModal } from '../components/EditLeadModal';
import type { LeadItem, LeadStatus } from '../types';

export function LeadsListPage({ defaultStatus }: { defaultStatus?: LeadStatus }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<LeadStatus | undefined>(defaultStatus);
  const [page, setPage] = useState(1);
  const [disqualifyTarget, setDisqualifyTarget] = useState<LeadItem | null>(null);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [qualifyTarget, setQualifyTarget] = useState<LeadItem | null>(null);
  const [qualifyBudgetRupees, setQualifyBudgetRupees] = useState('');
  const [qualifyTargetChitRupees, setQualifyTargetChitRupees] = useState('');
  const [qualifyDocReadiness, setQualifyDocReadiness] = useState<'COMPLETE' | 'PARTIAL' | 'PENDING'>('PARTIAL');
  const [qualifyNotes, setQualifyNotes] = useState('');
  const [editTarget, setEditTarget] = useState<LeadItem | null>(null);

  const { data: leadResponse, isLoading, error, refetch } = useLeads({
    status: statusFilter,
    page,
    limit: 20,
  });

  const startProgressMutation = useStartLeadProgress(qualifyTarget?.id ?? '');
  const qualifyMutation = useQualifyLead(qualifyTarget?.id ?? '');
  const disqualifyMutation = useDisqualifyLead(disqualifyTarget?.id ?? '');
  const deleteMutation = useDeleteLead();

  const leads = Array.isArray(leadResponse) ? leadResponse : (leadResponse?.data ?? []);
  const total = Array.isArray(leadResponse) ? leadResponse.length : (leadResponse?.meta?.total ?? leads.length);

  const handleStartProgress = async (_lead: LeadItem) => {
    try {
      await startProgressMutation.mutateAsync();
      toast({ type: 'success', title: 'Progress Started', description: `Lead marked as In Progress.` });
      void refetch();
    } catch (err: unknown) {
      console.error('Failed to start progress:', err);
      const msg = err instanceof Error ? err.message : 'Failed to start progress';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const handleQualify = async () => {
    if (!qualifyTarget) return;
    try {
      await qualifyMutation.mutateAsync({
        qualification: {
          budgetRupees: qualifyBudgetRupees ? new Decimal(qualifyBudgetRupees).toNumber() : undefined,
          targetChitRupees: qualifyTargetChitRupees ? new Decimal(qualifyTargetChitRupees).toNumber() : undefined,
          documentReadiness: qualifyDocReadiness,
          assessmentNotes: qualifyNotes.trim() || 'Criteria verified by staff',
          qualifiedAt: new Date().toISOString(),
        },
      });
      toast({ type: 'success', title: 'Lead Qualified', description: `${qualifyTarget.partyName || 'Lead'} successfully qualified.` });
      setQualifyTarget(null);
      setQualifyBudgetRupees('');
      setQualifyTargetChitRupees('');
      setQualifyNotes('');
      void refetch();
    } catch (err: unknown) {
      console.error('Failed to qualify lead:', err);
      const msg = err instanceof Error ? err.message : 'Failed to qualify lead';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const handleDisqualify = async () => {
    if (!disqualifyTarget || disqualifyReason.length < 10) return;
    try {
      await disqualifyMutation.mutateAsync({ reason: disqualifyReason });
      toast({ type: 'success', title: 'Lead Disqualified', description: 'Lead marked as disqualified.' });
      setDisqualifyTarget(null);
      setDisqualifyReason('');
      void refetch();
    } catch (err: unknown) {
      console.error('Failed to disqualify lead:', err);
      const msg = err instanceof Error ? err.message : 'Failed to disqualify lead';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ id });
      toast({ type: 'success', title: 'Lead Deleted', description: 'Lead archived and removed from active pipeline.' });
      void refetch();
    } catch (err: unknown) {
      console.error('Failed to delete lead:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete lead';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const getStatusBadge = (status: LeadStatus) => {
    const map: Record<LeadStatus, { bg: string; text: string; label: string }> = {
      NEW: { bg: 'bg-info-bg', text: 'text-info-text', label: 'New' },
      IN_PROGRESS: { bg: 'bg-warning-bg', text: 'text-warning-text', label: 'In Progress' },
      QUALIFIED: { bg: 'bg-success-bg', text: 'text-success-text', label: 'Qualified' },
      CONVERTED: { bg: 'bg-surface-2', text: 'text-text-primary', label: 'Converted' },
      DISQUALIFIED: { bg: 'bg-danger-bg', text: 'text-danger-text', label: 'Disqualified' },
    };
    const cfg = map[status] || { bg: 'bg-surface-1', text: 'text-text-muted', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
        {cfg.label}
      </span>
    );
  };

  const config = useMemo(() => defineListPage<LeadItem>({
    variant: 'data-table',
    resource: 'leads',
    emptyStateLabel: 'Leads',
    emptyStateDescription: 'No leads found in this stage. Create a new lead to kickstart customer engagement.',
    columns: [
      {
        id: 'partyName',
        header: 'Prospect / Customer Name',
        accessor: (row) => row.partyName || (row.partyId ? `Party (${row.partyId.slice(0, 8)})` : `Prospect (${row.id.slice(0, 8)})`),
        sortable: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'status',
        sortable: true,
      },
      {
        id: 'source',
        header: 'Source',
        accessor: 'source',
        sortable: true,
      },
      {
        id: 'createdAt',
        header: 'Created At',
        accessor: 'createdAt',
        sortable: true,
      },
    ],
    onRowClick: (row) => void navigate({ to: '/crm/$id', params: { id: row.id } }),
    rowActions: [
      {
        label: 'View Details',
        onClick: (row) => void navigate({ to: '/crm/$id', params: { id: row.id } }),
      },
      {
        label: 'Edit Lead',
        onClick: (row) => setEditTarget(row),
      },
      {
        label: 'Start Progress',
        hidden: (row) => row.status !== 'NEW',
        onClick: (row) => void handleStartProgress(row),
      },
      {
        label: 'Qualify',
        hidden: (row) => row.status !== 'IN_PROGRESS',
        onClick: (row) => setQualifyTarget(row),
      },
      {
        label: 'Disqualify',
        hidden: (row) => row.status !== 'IN_PROGRESS',
        onClick: (row) => setDisqualifyTarget(row),
      },
      {
        label: 'Enroll as Customer',
        hidden: (row) => row.status !== 'QUALIFIED',
        onClick: (row) => void navigate({ to: '/customers/new', search: { contactId: row.id } }),
      },
      {
        label: 'Delete Lead',
        variant: 'destructive',
        requiresConfirmation: true,
        onClick: (row) => void handleDeleteLead(row.id),
      },
    ],
  }), [navigate]);

  return (
    <div className="flex flex-col w-full h-full p-6 space-y-6" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="CRM Leads Pipeline"
        subtitle="Manage customer acquisition through canonical stages: New → In Progress → Qualified → Converted"
        action={
          <Button onClick={() => void navigate({ to: '/crm/new', search: { from: 'leads' } })}>
            + Create Lead
          </Button>
        }
      />

      {/* Stage Filter Tabs */}
      <div className="flex space-x-2 border-b border-border-base pb-3">
        {(['ALL', 'NEW', 'IN_PROGRESS', 'QUALIFIED', 'CONVERTED', 'DISQUALIFIED'] as const).map((st) => {
          const isSelected = st === 'ALL' ? !statusFilter : statusFilter === st;
          return (
            <Button
              key={st}
              variant={isSelected ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => {
                setStatusFilter(st === 'ALL' ? undefined : (st as LeadStatus));
                setPage(1);
              }}
            >
              {st === 'ALL' ? 'All Leads' : st.replace('_', ' ')}
            </Button>
          );
        })}
      </div>

      {/* Standardized Data Table with Row Actions */}
      <DataTable
        config={config}
        data={leads}
        total={total}
        page={page}
        limit={20}
        onPageChange={setPage}
        loading={isLoading}
        error={error instanceof Error ? error.message : undefined}
        onRetry={() => void refetch()}
        customRenderers={{
          partyName: (row: LeadItem) => (
            <div>
              <Link to="/crm/$id" params={{ id: row.id }} className="hover:underline text-accent-600 font-semibold block">
                {row.partyName || (row.partyId ? `Party (${row.partyId.slice(0, 8)})` : `Prospect (${row.id.slice(0, 8)})`)}
              </Link>
              {row.partyMobile && (
                <span className="text-xs text-text-muted block font-mono mt-0.5">{row.partyMobile}</span>
              )}
            </div>
          ),
          status: (row: LeadItem) => getStatusBadge(row.status),
          source: (row: LeadItem) => <span className="text-text-secondary">{row.source}</span>,
          createdAt: (row: LeadItem) => <span className="text-text-muted">{new Date(row.createdAt).toLocaleDateString()}</span>,
        }}
      />

      {/* Edit Lead Modal */}
      {editTarget && (
        <EditLeadModal
          lead={editTarget}
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => void refetch()}
        />
      )}

      {/* Qualify Dialog */}
      {qualifyTarget && (
        <Dialog open={!!qualifyTarget} onOpenChange={(open) => !open && setQualifyTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Qualify Lead Assessment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2 text-sm">
              <p className="text-text-muted">
                Record staff qualification assessment criteria for {qualifyTarget.partyName || 'this prospect'} before advancing to KYC and enrollment.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-primary mb-1">Monthly Budget (INR)</label>
                  <Input
                    type="number"
                    min={0}
                    value={qualifyBudgetRupees}
                    onChange={(e) => setQualifyBudgetRupees(e.target.value)}
                    placeholder="e.g. 10000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-primary mb-1">Target Chit Value (INR)</label>
                  <Input
                    type="number"
                    min={0}
                    value={qualifyTargetChitRupees}
                    onChange={(e) => setQualifyTargetChitRupees(e.target.value)}
                    placeholder="e.g. 500000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-primary mb-1">Documentation Readiness</label>
                <select
                  value={qualifyDocReadiness}
                  onChange={(e) => setQualifyDocReadiness(e.target.value as 'COMPLETE' | 'PARTIAL' | 'PENDING')}
                  className="w-full p-2 text-sm border border-border-base rounded-md"
                >
                  <option value="COMPLETE">Complete (Aadhaar & PAN verified/ready)</option>
                  <option value="PARTIAL">Partial (Under collection / pending copies)</option>
                  <option value="PENDING">Pending (Initial discussion only)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-primary mb-1">Qualification Remarks</label>
                <textarea
                  value={qualifyNotes}
                  onChange={(e) => setQualifyNotes(e.target.value)}
                  placeholder="Record assessment rationale, subscriber eligibility, or preferred terms..."
                  className="w-full h-20 p-2 text-sm border border-border-base rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setQualifyTarget(null)}>Cancel</Button>
              <Button onClick={() => void handleQualify()}>Confirm Qualification</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Disqualify Dialog */}
      {disqualifyTarget && (
        <Dialog open={!!disqualifyTarget} onOpenChange={(open) => !open && setDisqualifyTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disqualify Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <label className="text-sm font-medium">Disqualification Reason (min 10 characters):</label>
              <textarea
                value={disqualifyReason}
                onChange={(e) => setDisqualifyReason(e.target.value)}
                placeholder="Reason for disqualification (e.g. Ineligible location, Not interested in terms)"
                className="w-full h-24 p-2.5 text-sm border border-border-base rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDisqualifyTarget(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={disqualifyReason.trim().length < 10}
                onClick={() => void handleDisqualify()}
              >
                Confirm Disqualification
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
